"use client";

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { rashis } from "@ishvaram/domain-data";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { edgeRead } from "@/lib/api/_fetch";
import { getTodayISTDateString } from "@/lib/ist-date";
import RashifalAudioPlayer from "@/components/RashifalAudioPlayer";
import {
  assertEngineDailyHoroscope,
  formatEngineDate,
  type EngineDailyHoroscope,
  type EngineRashiPicture,
} from "@/app/daily-horoscope/daily-engine.server";

/**
 * Daily gochar studio for /rashifal/ and /hi/aaj-ka-rashifal/.
 *
 * Every per-rashi value shown here is the engine's picture for the day
 * (`GET /rashifal/engine/daily`): Moon rashi, nakshatra, house from the rashi
 * and the governed transit cards. The studio holds no forecast, timing, colour
 * or house literal of its own — the labels below are
 * rashi names and glyphs only. When no engine day is available it says so and
 * shows nothing that could be mistaken for a reading.
 */

const RASHI_GLYPHS: Readonly<Record<string, string>> = {
  mesh: "♈",
  vrishabh: "♉",
  mithun: "♊",
  kark: "♋",
  singh: "♌",
  kanya: "♍",
  tula: "♎",
  vrishchik: "♏",
  dhanu: "♐",
  makar: "♑",
  kumbh: "♒",
  meen: "♓",
};

export const RASHI_SELECTOR = rashis.map((rashi) => ({
  id: rashi.id,
  name: rashi.name_en,
  hindi: rashi.name_hi,
  symbol: RASHI_GLYPHS[rashi.id] ?? "✦",
}));

/** Split an engine picture into its lines: the lead sentence, then one fact per line. */
export function engineLines(content: string): string[] {
  return content
    .split(/\n{2,}/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const NO_CHANGE = () => () => {};

async function fetchTodayEngineHoroscope(
  language: "en" | "hi"
): Promise<EngineDailyHoroscope | null> {
  try {
    const raw = await edgeRead<unknown>(
      "rashifal/engine/daily",
      `language=${language}&day=today`,
      8_000
    );
    return assertEngineDailyHoroscope(raw);
  } catch {
    return null;
  }
}

export default function DailyGocharRashifalStudio({
  isHindi = false,
  initialSnapshot = null,
}: {
  isHindi?: boolean;
  /** The server's engine day, already checked against the hub's date stamp. */
  initialSnapshot?: EngineDailyHoroscope | null;
}) {
  const [selectedRashiId, setSelectedRashiId] = useState<string>(RASHI_SELECTOR[0]?.id ?? "mesh");
  const [snapshot, setSnapshot] = useState<EngineDailyHoroscope | null>(initialSnapshot);
  // The viewer's IST civil day. The server value is the shipped engine day (or
  // the render's stamp), so hydration agrees with the HTML; after hydration the
  // client value is the viewer's own day and a prerendered hub that outlived
  // its day stops showing that day's picture as today's.
  const viewerToday = useSyncExternalStore(
    NO_CHANGE,
    getTodayISTDateString,
    () => initialSnapshot?.today ?? getTodayISTDateString()
  );
  const live = snapshot && snapshot.today === viewerToday ? snapshot : null;

  useEffect(() => {
    // Missing (rendered before the engine endpoint existed) or stale (ISR
    // outlived the day): fetch the viewer's day. A failed fetch leaves the
    // degraded state and does not retry on its own.
    if (live) return;
    let cancelled = false;
    void fetchTodayEngineHoroscope(isHindi ? "hi" : "en").then((fresh) => {
      if (!cancelled && fresh && fresh.today === viewerToday) setSnapshot(fresh);
    });
    return () => {
      cancelled = true;
    };
  }, [isHindi, live, viewerToday]);

  const rashi = useMemo(
    () => RASHI_SELECTOR.find((r) => r.id === selectedRashiId) ?? RASHI_SELECTOR[0],
    [selectedRashiId]
  );
  const picture = live?.rashis.find((r) => r.rashi === rashi.id) ?? null;

  const handleConsult = () => {
    safeSessionSet(
      "ishvaram_intent_context",
      JSON.stringify({
        source: isHindi ? "/hi/aaj-ka-rashifal" : "/rashifal",
        rashi: rashi.name,
      })
    );
  };

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/rashifal"),
    "/rashifal",
    isHindi ? "hi" : "en"
  );
  const shareText = encodeURIComponent(
    `Today's Vedic Rashifal for ${rashi.name} (${rashi.hindi}) on Ishvaram. https://ishvaram.com/rashifal/`
  );
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${shareText}`;

  return (
    <div
      className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs"
      data-engine-state={live ? "ready" : "unavailable"}
      data-engine-date={live?.date}
    >
      <StudioHeader isHindi={isHindi} whatsappShareUrl={whatsappShareUrl} />
      <RashiSelectorGrid selectedId={rashi.id} onSelect={setSelectedRashiId} isHindi={isHindi} />
      <RashiTransitCard
        rashi={rashi}
        picture={picture}
        snapshot={live}
        isHindi={isHindi}
        chatHref={chatHref}
        onConsult={handleConsult}
      />
    </div>
  );
}

function StudioHeader({
  isHindi,
  whatsappShareUrl,
}: {
  isHindi: boolean;
  whatsappShareUrl: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border-default">
      <div>
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
          {isHindi ? "दैनिक गोचर आधारित राशिफल" : "Daily Planetary Gochar Engine"}
        </span>
        <h2 className="text-2xl font-bold font-serif text-text-primary mt-1">
          {isHindi
            ? "आज का वैदिक चंद्र राशिफल व गोचर विश्लेषण"
            : "Today's Vedic Moon-Sign Rashifal & Transit Studio"}
        </h2>
      </div>

      <a
        href={whatsappShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-3.5 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors shrink-0"
      >
        <span>📱</span>
        <span>{isHindi ? "WhatsApp पर शेयर करें" : "Share on WhatsApp"}</span>
      </a>
    </div>
  );
}

function RashiSelectorGrid({
  selectedId,
  onSelect,
  isHindi,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  isHindi: boolean;
}) {
  return (
    <div className="my-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
      {RASHI_SELECTOR.map((item) => {
        const isSelected = item.id === selectedId;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            aria-pressed={isSelected}
            className={`p-2.5 rounded-xl border text-center transition-all ${
              isSelected
                ? "border-intent-brand bg-surface-brand-subtle shadow-xs ring-2 ring-intent-brand/20 text-text-primary"
                : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
            }`}
          >
            <span className="text-lg block mb-0.5">{item.symbol}</span>
            <span className="text-body-small font-bold block truncate">
              {isHindi ? item.hindi : item.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function EnginePicture({
  picture,
  snapshot,
  isHindi,
  rashiLabel,
}: {
  picture: EngineRashiPicture;
  snapshot: EngineDailyHoroscope;
  isHindi: boolean;
  rashiLabel: string;
}) {
  const [lead, ...facts] = engineLines(picture.content);
  return (
    <div className="my-5 p-4 rounded-xl bg-surface-raised border border-border-default">
      <p className="text-caption text-text-muted" data-engine-dateline>
        {isHindi ? "इंजन गोचर चित्र:" : "Engine transit picture:"}{" "}
        {formatEngineDate(snapshot.date, isHindi ? "hi" : "en")}
      </p>
      {lead && (
        <p className="mt-2 text-body-small leading-relaxed text-text-primary font-medium">{lead}</p>
      )}
      {facts.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-body-small text-text-secondary">
          {facts.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
      <div className="mt-3 pt-3 border-t border-border-default">
        <RashifalAudioPlayer
          textToSpeak={picture.content}
          rashiName={rashiLabel}
          isHindi={isHindi}
        />
      </div>
    </div>
  );
}

function EngineUnavailable({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="my-5 p-4 rounded-xl bg-surface-raised border border-border-default">
      <p className="text-body-small leading-relaxed text-text-secondary">
        {isHindi
          ? "आज का इंजन गोचर चित्र अभी लोड नहीं हो सका, इसलिए यहाँ कोई फल नहीं दिखाया गया है। अपनी राशि का पेज खोलें या पहले पंचांग में चन्द्रमा की स्थिति देखें।"
          : "Today's engine transit picture could not be loaded, so no reading is shown here. Open your rashi's page, or check the Moon's position in the Panchang first."}
      </p>
    </div>
  );
}

function RashiTransitCard({
  rashi,
  picture,
  snapshot,
  isHindi,
  chatHref,
  onConsult,
}: {
  rashi: (typeof RASHI_SELECTOR)[number];
  picture: EngineRashiPicture | null;
  snapshot: EngineDailyHoroscope | null;
  isHindi: boolean;
  chatHref: string;
  onConsult: () => void;
}) {
  const rashiLabel = isHindi ? rashi.hindi : rashi.name;
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{rashi.symbol}</span>
          <div>
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {isHindi
                ? `${rashi.hindi} राशि — आज का गोचर फल`
                : `${rashi.name} (${rashi.hindi}) — Today's Transit`}
            </h3>
            <p className="text-caption text-text-secondary mt-0.5">
              {isHindi
                ? "चन्द्र राशि के अनुसार, Swiss Ephemeris ग्रह स्थितियों से"
                : "By Chandra rashi, from Swiss Ephemeris planetary positions"}
            </p>
          </div>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {isHindi ? "व्यक्तिगत कुंडली से पूछें ✦" : "Analyze My Chart with AI ✦"}
        </a>
      </div>

      {picture && snapshot ? (
        <EnginePicture
          picture={picture}
          snapshot={snapshot}
          isHindi={isHindi}
          rashiLabel={rashiLabel}
        />
      ) : (
        <EngineUnavailable isHindi={isHindi} />
      )}

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <Link
          href={`/rashifal/${rashi.id}/`}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          {isHindi
            ? `${rashi.hindi} राशि का पूरा राशिफल पढ़ें →`
            : `Read the full ${rashi.name} rashifal →`}
        </Link>
        <Link
          href="/free-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1 shrink-0"
        >
          {isHindi ? "मुफ्त जन्म कुंडली बनाएँ →" : "Calculate Full Birth Chart →"}
        </Link>
      </div>
    </div>
  );
}
